import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { CheckCircle2 } from 'lucide-react';
import Footer from '@/components/Footer';
import { Link } from 'react-router-dom';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const from = (location.state as any)?.from?.pathname || '/settings';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter both email and password",
      });
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        const { data: isAdmin, error: adminError } = await supabase.rpc('is_admin');
        
        if (adminError) throw adminError;
        
        if (isAdmin) {
          toast({
            title: "Welcome back!",
            description: "Successfully logged in.",
          });
          setPhoneVerified(true);
          setTimeout(() => {
            setPhoneVerified(false);
            navigate(from, { replace: true });
          }, 1500);
        } else {
          await supabase.auth.signOut();
          throw new Error("Unauthorized: Admin access required");
        }
      }
    } catch (error: any) {
      console.error('Login error:', error);
      
      if (error.message?.includes('infinite recursion') || error.message?.includes('row level security')) {
        setShowInstructions(true);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message || 'An error occurred during login',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (phoneVerified) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-grow flex items-center justify-center bg-gray-50">
          <div className="p-8 bg-white rounded-lg shadow-md w-full max-w-md text-center">
            <CheckCircle2 className="mx-auto h-16 w-16 text-green-500 mb-4" />
            <h1 className="text-2xl font-bold mb-2">Admin Access Verified</h1>
            <p className="text-gray-600 mb-4">Your account is linked to an admin profile.</p>
            <p className="text-gray-600">Redirecting to admin dashboard...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-3xl font-bold text-gray-900">
            Admin Login
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Sign in to access the admin dashboard
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Link
              to="/admin/reset-password"
              className="text-sm text-primary hover:text-primary/90"
            >
              Forgot your password?
            </Link>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading}
          >
            {loading ? "Signing in..." : "Sign in"}
          </Button>
        </form>
      </div>

      <Footer />

      <Dialog open={showInstructions} onOpenChange={setShowInstructions}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Database Configuration Issue</DialogTitle>
            <DialogDescription>
              <p className="mb-4">
                There appears to be an infinite recursion in the Row Level Security policies for the admin_users table.
              </p>
              <p className="mb-4">
                To fix this issue, you need to update the RLS policies in your Supabase project:
              </p>
              <ol className="list-decimal pl-5 space-y-2 mb-4">
                <li>Go to your Supabase dashboard</li>
                <li>Navigate to the SQL Editor</li>
                <li>Run the following SQL commands:</li>
              </ol>
              <pre className="bg-gray-100 p-2 rounded text-xs overflow-auto">
{`-- 1. Drop any problematic RLS policies on the admin_users table
DROP POLICY IF EXISTS "Admins can view admin_users" ON public.admin_users;

-- 2. Create a new policy that uses the is_admin function (which should already exist)
CREATE POLICY "Admins can view admin_users" 
ON public.admin_users 
FOR ALL 
USING (auth.uid() IN (SELECT id FROM public.admin_users));`}
              </pre>
              <p className="mt-4">
                After running these commands, please try logging in again.
              </p>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLogin;
